import React, {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { GoogleMap, Polygon } from "@react-google-maps/api";
import {
  TerraDraw,
  TerraDrawPolygonMode,
  TerraDrawSelectMode,
} from "terra-draw";
import { TerraDrawGoogleMapsAdapter } from "terra-draw-google-maps-adapter";
import { useGoogleMaps } from "@/utils/googleMaps.jsx";

/**
 * Normalize pasted GeoJSON (geometry, Feature, or FeatureCollection)
 * into a Polygon/MultiPolygon geometry, or null if invalid.
 */
export function parseGeoJsonGeometry(text) {
  const raw = String(text || "").trim();
  if (!raw) {
    return { geometry: null, error: null };
  }

  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return { geometry: null, error: "Invalid JSON" };
  }

  if (parsed?.type === "Feature" && parsed.geometry) {
    parsed = parsed.geometry;
  } else if (parsed?.type === "FeatureCollection" && parsed.features?.[0]?.geometry) {
    parsed = parsed.features[0].geometry;
  }

  if (!parsed || typeof parsed !== "object") {
    return { geometry: null, error: "Not a GeoJSON object" };
  }

  const type = String(parsed.type || "").trim();
  if (type !== "Polygon" && type !== "MultiPolygon") {
    return {
      geometry: null,
      error: "Expected Polygon or MultiPolygon geometry",
    };
  }

  if (!Array.isArray(parsed.coordinates) || !parsed.coordinates.length) {
    return { geometry: null, error: "Missing coordinates" };
  }

  return {
    geometry: { type, coordinates: parsed.coordinates },
    error: null,
  };
}

export function stringifyGeoJsonGeometry(geometry) {
  if (!geometry) return "";
  return JSON.stringify(geometry, null, 2);
}

function ringToPath(ring) {
  if (!Array.isArray(ring)) return [];
  return ring
    .map((coord) => {
      if (!Array.isArray(coord) || coord.length < 2) return null;
      const lng = Number(coord[0]);
      const lat = Number(coord[1]);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
      return { lat, lng };
    })
    .filter(Boolean);
}

/** Convert Polygon / MultiPolygon coordinates into Google Maps polygon path arrays. */
function geometryToPaths(geometry) {
  if (!geometry?.coordinates) return [];

  if (geometry.type === "Polygon") {
    const outer = ringToPath(geometry.coordinates[0]);
    return outer.length ? [outer] : [];
  }

  if (geometry.type === "MultiPolygon") {
    return geometry.coordinates
      .map((poly) => ringToPath(poly?.[0]))
      .filter((path) => path.length > 0);
  }

  return [];
}

function boundsFromPaths(paths) {
  const all = paths.flat();
  if (!all.length) return null;
  let minLat = Infinity;
  let maxLat = -Infinity;
  let minLng = Infinity;
  let maxLng = -Infinity;
  for (const p of all) {
    minLat = Math.min(minLat, p.lat);
    maxLat = Math.max(maxLat, p.lat);
    minLng = Math.min(minLng, p.lng);
    maxLng = Math.max(maxLng, p.lng);
  }
  return { minLat, maxLat, minLng, maxLng };
}

function geometryKey(geometry) {
  return geometry ? JSON.stringify(geometry) : "";
}

function geometryToFeatures(geometry) {
  if (!geometry?.coordinates) return [];

  if (geometry.type === "Polygon") {
    return [
      {
        type: "Feature",
        properties: { mode: "polygon" },
        geometry: {
          type: "Polygon",
          coordinates: geometry.coordinates,
        },
      },
    ];
  }

  if (geometry.type === "MultiPolygon") {
    return geometry.coordinates.map((coords) => ({
      type: "Feature",
      properties: { mode: "polygon" },
      geometry: {
        type: "Polygon",
        coordinates: coords,
      },
    }));
  }

  return [];
}

function snapshotToGeometry(snapshot) {
  const polygons = (snapshot || []).filter(
    (f) =>
      f?.geometry?.type === "Polygon" &&
      f?.properties?.mode === "polygon" &&
      Array.isArray(f.geometry.coordinates)
  );

  if (!polygons.length) return null;
  if (polygons.length === 1) {
    return {
      type: "Polygon",
      coordinates: polygons[0].geometry.coordinates,
    };
  }
  return {
    type: "MultiPolygon",
    coordinates: polygons.map((p) => p.geometry.coordinates),
  };
}

const DEFAULT_CENTER = { lat: 39.8283, lng: -98.5795 }; // continental US

const polygonOptions = {
  fillColor: "#6366f1",
  fillOpacity: 0.25,
  strokeColor: "#4f46e5",
  strokeOpacity: 0.9,
  strokeWeight: 2,
};

const POLYGON_STYLES = {
  fillColor: "#6366f1",
  fillOpacity: 0.25,
  outlineColor: "#4f46e5",
  outlineWidth: 2,
};

/**
 * Live map preview for a GeoJSON Polygon / MultiPolygon.
 * When `editable`, uses Terra Draw for polygon draw / select / vertex edit.
 */
function GeoJsonPreviewMap({
  geometry = null,
  geojsonText = "",
  height = 320,
  className = "",
  editable = false,
  onGeometryChange = null,
}) {
  const { isLoaded } = useGoogleMaps();
  const reactId = useId();
  const mapDomId = `geojson-preview-map-${reactId.replace(/:/g, "")}`;

  const mapRef = useRef(null);
  const drawRef = useRef(null);
  const syncingFromPropsRef = useRef(false);
  const lastSyncedKeyRef = useRef("");
  const onGeometryChangeRef = useRef(onGeometryChange);
  onGeometryChangeRef.current = onGeometryChange;

  const [mapReady, setMapReady] = useState(false);
  const [drawReady, setDrawReady] = useState(false);
  const [activeMode, setActiveMode] = useState("polygon");

  const resolvedGeometry = useMemo(() => {
    if (geometry) return geometry;
    return parseGeoJsonGeometry(geojsonText).geometry;
  }, [geometry, geojsonText]);

  const paths = useMemo(
    () => geometryToPaths(resolvedGeometry),
    [resolvedGeometry]
  );

  const hasShape = paths.length > 0;
  const resolvedKey = geometryKey(resolvedGeometry);

  const center = useMemo(() => {
    const b = boundsFromPaths(paths);
    if (!b) return DEFAULT_CENTER;
    return {
      lat: (b.minLat + b.maxLat) / 2,
      lng: (b.minLng + b.maxLng) / 2,
    };
  }, [paths]);

  const zoom = useMemo(() => {
    const b = boundsFromPaths(paths);
    if (!b) return 4;
    const latDiff = Math.max(b.maxLat - b.minLat, 0.001);
    const lngDiff = Math.max(b.maxLng - b.minLng, 0.001);
    const maxDiff = Math.max(latDiff, lngDiff);
    if (maxDiff > 20) return 3;
    if (maxDiff > 8) return 5;
    if (maxDiff > 3) return 6;
    if (maxDiff > 1) return 7;
    if (maxDiff > 0.5) return 9;
    if (maxDiff > 0.2) return 10;
    if (maxDiff > 0.1) return 11;
    if (maxDiff > 0.05) return 12;
    if (maxDiff > 0.02) return 13;
    return 14;
  }, [paths]);

  const fitToPaths = useCallback((map, nextPaths) => {
    if (!map || !window.google?.maps || !nextPaths?.length) return;
    const bounds = new window.google.maps.LatLngBounds();
    nextPaths.flat().forEach((p) => bounds.extend(p));
    map.fitBounds(bounds, 40);
  }, []);

  useEffect(() => {
    if (!mapReady || !mapRef.current || !paths.length) return;
    // While actively drawing a new polygon, don't fight the user's viewport.
    if (editable && activeMode === "polygon" && !hasShape) return;
    fitToPaths(mapRef.current, paths);
  }, [mapReady, paths, editable, activeMode, hasShape, fitToPaths]);

  const emitFromDraw = useCallback(() => {
    const draw = drawRef.current;
    if (!draw || syncingFromPropsRef.current) return;
    const next = snapshotToGeometry(draw.getSnapshot());
    const key = geometryKey(next);
    if (key === lastSyncedKeyRef.current) return;
    lastSyncedKeyRef.current = key;
    onGeometryChangeRef.current?.(next);
  }, []);

  const loadGeometryIntoDraw = useCallback(
    (draw, nextGeometry) => {
      if (!draw) return;
      const key = geometryKey(nextGeometry);
      syncingFromPropsRef.current = true;
      try {
        draw.clear();
        const features = geometryToFeatures(nextGeometry);
        if (features.length) {
          draw.addFeatures(features);
        }
        lastSyncedKeyRef.current = key;
      } finally {
        // Defer so Terra Draw's own change events from addFeatures settle first.
        queueMicrotask(() => {
          syncingFromPropsRef.current = false;
        });
      }
    },
    []
  );

  // Initialize Terra Draw once the Google map projection is available.
  useEffect(() => {
    if (!editable || !mapReady || !mapRef.current || !window.google?.maps) {
      return undefined;
    }

    const map = mapRef.current;
    let draw = null;
    let cancelled = false;
    let projectionListener = null;
    let started = false;

    const handleReady = () => {
      if (cancelled || !draw) return;
      setDrawReady(true);
      loadGeometryIntoDraw(draw, resolvedGeometry);
      const mode = resolvedGeometry ? "select" : "polygon";
      draw.setMode(mode);
      setActiveMode(mode);
    };

    const handleFinish = () => {
      if (cancelled || syncingFromPropsRef.current) return;
      emitFromDraw();
      if (drawRef.current) {
        drawRef.current.setMode("select");
        setActiveMode("select");
      }
    };

    const handleChange = (_ids, type, context) => {
      if (cancelled || syncingFromPropsRef.current) return;
      if (context?.origin === "api") return;
      // Ignore provisional create noise while drawing; finish handles completion.
      if (type === "create") return;
      if (type === "delete" || type === "update") {
        emitFromDraw();
      }
    };

    const initDraw = () => {
      if (cancelled || started || !map.getProjection()) return;
      started = true;

      draw = new TerraDraw({
        adapter: new TerraDrawGoogleMapsAdapter({
          lib: window.google.maps,
          map,
          coordinatePrecision: 9,
        }),
        modes: [
          new TerraDrawPolygonMode({
            styles: POLYGON_STYLES,
          }),
          new TerraDrawSelectMode({
            flags: {
              polygon: {
                feature: {
                  draggable: true,
                  coordinates: {
                    midpoints: true,
                    draggable: true,
                    deletable: true,
                  },
                },
              },
            },
          }),
        ],
      });

      drawRef.current = draw;
      draw.on("ready", handleReady);
      draw.on("finish", handleFinish);
      draw.on("change", handleChange);
      draw.start();
    };

    if (map.getProjection()) {
      initDraw();
    } else {
      projectionListener = map.addListener("projection_changed", () => {
        initDraw();
      });
    }

    return () => {
      cancelled = true;
      if (projectionListener && window.google?.maps?.event) {
        window.google.maps.event.removeListener(projectionListener);
      }
      if (draw) {
        try {
          draw.off("ready", handleReady);
          draw.off("finish", handleFinish);
          draw.off("change", handleChange);
          draw.stop();
        } catch {
          // ignore teardown races
        }
      }
      if (drawRef.current === draw) {
        drawRef.current = null;
      }
      setDrawReady(false);
    };
    // resolvedGeometry is synced in a separate effect once draw is ready
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editable, mapReady, emitFromDraw, loadGeometryIntoDraw]);

  // Sync textarea / prop geometry into Terra Draw (when not originating from draw).
  useEffect(() => {
    if (!editable || !drawReady) return;
    const draw = drawRef.current;
    if (!draw) return;
    if (resolvedKey === lastSyncedKeyRef.current) return;
    loadGeometryIntoDraw(draw, resolvedGeometry);
    const mode = resolvedGeometry ? "select" : "polygon";
    if (draw.getMode() !== mode) {
      draw.setMode(mode);
      setActiveMode(mode);
    }
  }, [editable, drawReady, resolvedKey, resolvedGeometry, loadGeometryIntoDraw]);

  const setDrawMode = useCallback((mode) => {
    const draw = drawRef.current;
    if (!draw || !drawReady) return;
    draw.setMode(mode);
    setActiveMode(mode);
  }, [drawReady]);

  const handleStartDraw = useCallback(() => {
    const draw = drawRef.current;
    if (!draw || !drawReady) return;
    syncingFromPropsRef.current = true;
    try {
      draw.clear();
      lastSyncedKeyRef.current = "";
      onGeometryChangeRef.current?.(null);
    } finally {
      queueMicrotask(() => {
        syncingFromPropsRef.current = false;
      });
    }
    draw.setMode("polygon");
    setActiveMode("polygon");
  }, [drawReady]);

  const handleSelectMode = useCallback(() => {
    setDrawMode("select");
  }, [setDrawMode]);

  const handleClear = useCallback(() => {
    const draw = drawRef.current;
    if (!draw || !drawReady) {
      onGeometryChangeRef.current?.(null);
      return;
    }
    syncingFromPropsRef.current = true;
    try {
      draw.clear();
      lastSyncedKeyRef.current = "";
      onGeometryChangeRef.current?.(null);
    } finally {
      queueMicrotask(() => {
        syncingFromPropsRef.current = false;
      });
    }
    draw.setMode("polygon");
    setActiveMode("polygon");
  }, [drawReady]);

  const mapContainerStyle = {
    width: "100%",
    height: typeof height === "number" ? `${height}px` : height,
  };

  if (!isLoaded) {
    return (
      <div
        className={`flex items-center justify-center rounded-lg border border-slate-200 bg-slate-100 text-sm text-slate-500 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-400 ${className}`}
        style={{ height: typeof height === "number" ? `${height}px` : height }}
      >
        Loading map…
      </div>
    );
  }

  return (
    <div className={`space-y-2 ${className}`}>
      {editable ? (
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={handleStartDraw}
            disabled={!drawReady}
            className={`btn-xs rounded px-2.5 py-1 text-xs font-medium disabled:opacity-50 ${
              activeMode === "polygon"
                ? "bg-indigo-600 text-white"
                : "bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-200 dark:hover:bg-slate-600"
            }`}
          >
            {hasShape ? "Redraw" : "Draw polygon"}
          </button>
          {hasShape ? (
            <button
              type="button"
              onClick={handleSelectMode}
              disabled={!drawReady}
              className={`btn-xs rounded px-2.5 py-1 text-xs font-medium disabled:opacity-50 ${
                activeMode === "select"
                  ? "bg-indigo-600 text-white"
                  : "bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-200 dark:hover:bg-slate-600"
              }`}
            >
              Edit
            </button>
          ) : null}
          {hasShape || activeMode === "polygon" ? (
            <button
              type="button"
              onClick={handleClear}
              disabled={!drawReady}
              className="btn-xs rounded px-2.5 py-1 text-xs font-medium text-rose-600 hover:bg-rose-50 disabled:opacity-50 dark:text-rose-400 dark:hover:bg-rose-400/10"
            >
              Clear
            </button>
          ) : null}
          <span className="text-xs text-slate-500 dark:text-slate-400">
            {!drawReady
              ? "Preparing drawing tools…"
              : activeMode === "polygon"
                ? "Click to place vertices; click the first point (or double-click) to close."
                : hasShape
                  ? "Click a shape to select, then drag vertices or the polygon."
                  : "Draw on the map or paste GeoJSON."}
          </span>
        </div>
      ) : null}

      <div className="overflow-hidden rounded-lg border border-slate-200 dark:border-slate-600 relative">
        <GoogleMap
          id={mapDomId}
          mapContainerStyle={mapContainerStyle}
          center={center}
          zoom={zoom}
          onLoad={(map) => {
            mapRef.current = map;
            setMapReady(true);
          }}
          onUnmount={() => {
            mapRef.current = null;
            setMapReady(false);
          }}
          options={{
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: true,
            zoomControl: true,
            scrollwheel: true,
            gestureHandling: "greedy",
            clickableIcons: false,
            draggableCursor:
              editable && activeMode === "polygon" ? "crosshair" : undefined,
          }}
        >
          {/* Read-only preview only — editable mode is rendered by Terra Draw. */}
          {!editable &&
            paths.map((path, idx) => (
              <Polygon
                key={`poly-${idx}-${path.length}`}
                paths={path}
                options={{
                  ...polygonOptions,
                  editable: false,
                  draggable: false,
                }}
              />
            ))}
        </GoogleMap>

        {!hasShape && !editable ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-slate-50/90 dark:bg-slate-900/80 pointer-events-none px-4 text-center">
            <p className="text-sm font-medium text-slate-600 dark:text-slate-300">
              Map preview
            </p>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400 max-w-[16rem]">
              Paste a valid Polygon or MultiPolygon to see it here.
            </p>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default GeoJsonPreviewMap;
